'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { SystemNotification } from '@/shared/schema/system-notification';
import { StreamingLLMOutput } from '@/components/shared-markdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getNotificationStyle } from './notification-styles';
import { X, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlassGlowButton, GradientDivider } from '@/shared/ui/glass';
import { cn } from '@/lib/utils';
interface NotificationBannerProps {
  notifications: SystemNotification[];
  onDismiss: (notificationId: string) => void;
  className?: string;
  /**
   * "overlay" (default) — fixed to the viewport below the navbar, works on any
   * screen regardless of the page's layout. "inline" — renders in normal flow,
   * used by admin previews.
   */
  variant?: 'overlay' | 'inline';
}
export function NotificationBanner(t0) {
  const $ = _c(53);
  const {
    notifications,
    onDismiss,
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "overlay" : t1;
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [isStackExpanded, setIsStackExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  if (notifications.length === 0) {
    return null;
  }
  const hasMoreNotifications = notifications.length > 3;
  const sortByType = _temp;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let visibleNotifications;
  if ($[2] !== className || $[3] !== isMobile || $[4] !== isStackExpanded || $[5] !== notifications || $[6] !== onDismiss || $[7] !== variant) {
    const sortedNotifications = [...notifications].sort(sortByType);
    visibleNotifications = isStackExpanded ? sortedNotifications : sortedNotifications.slice(0, 3);
    const t9 = variant === "overlay" ? "fixed top-8 left-0 right-0 z-50 pointer-events-none" : "relative";
    if ($[14] !== className || $[15] !== t9) {
      t8 = cn("flex justify-center px-4", t9, className);
      $[14] = className;
      $[15] = t9;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    const t10 = !isMobile && "group hover:space-y-2";
    if ($[17] !== t10) {
      t4 = cn("relative transition-all duration-300 ease-out w-full max-w-2xl pointer-events-auto", t10);
      $[17] = t10;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    if ($[19] !== isMobile) {
      t5 = () => !isMobile && setIsStackExpanded(true);
      t6 = () => !isMobile && setIsStackExpanded(false);
      $[19] = isMobile;
      $[20] = t5;
      $[21] = t6;
    } else {
      t5 = $[20];
      t6 = $[21];
    }
    let t11;
    if ($[22] !== isStackExpanded || $[23] !== onDismiss) {
      t11 = (notification, index) => {
        const style = getNotificationStyle(notification.severity);
        const SeverityIcon = style.icon;
        const isStacked = index > 0 && !isStackExpanded;
        const stackIndex = Math.min(index, 2);
        const yOffset = isStacked ? stackIndex * 4 : 0;
        const scale = isStacked ? (100 - stackIndex * 2) / 100 : 1;
        const opacity = isStacked ? (100 - stackIndex * 10) / 100 : 1;
        return <div key={notification.id} className={cn("absolute inset-x-0 rounded-2xl bg-white/90 backdrop-blur-xl p-4 transition-all duration-300 ease-out", isStacked ? style.stackedShadow : style.cardShadow, isStackExpanded && "relative")} style={{
          transform: isStackExpanded ? "translateY(0) scale(1)" : `translateY(${yOffset}px) scale(${scale})`,
          opacity: isStackExpanded ? 1 : opacity,
          zIndex: 100 - index,
          marginBottom: isStackExpanded ? "0.5rem" : "0"
        }}><div className="flex items-start justify-between gap-3"><div className="flex items-start gap-3 flex-1 min-w-0"><span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", style.iconTile)}><SeverityIcon className="h-4 w-4" /></span><div className="flex-1 min-w-0"><p className="text-sm font-semibold text-[#091E42] mb-0.5">{notification.title}</p><p className="text-sm text-gray-600">{notification.description}</p>{notification.type === "extended_banner" && <button type="button" onClick={() => setExpandedNotification(notification)} className={cn("mt-1.5 inline-flex items-center gap-0.5 text-xs font-medium transition-colors", style.accentText)}>View details<ChevronRight className="h-3 w-3" /></button>}</div></div><button type="button" onClick={() => onDismiss(notification.id)} className="shrink-0 rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-black/5 active:scale-[0.97] transition-all" aria-label="Dismiss notification"><X className="h-4 w-4" /></button></div></div>;
      };
      $[22] = isStackExpanded;
      $[23] = onDismiss;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    t7 = visibleNotifications.map(t11);
    $[2] = className;
    $[3] = isMobile;
    $[4] = isStackExpanded;
    $[5] = notifications;
    $[6] = onDismiss;
    $[7] = variant;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = visibleNotifications;
  } else {
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    visibleNotifications = $[13];
  }
  let t9;
  if ($[25] !== hasMoreNotifications || $[26] !== isMobile || $[27] !== isStackExpanded || $[28] !== notifications.length) {
    t9 = isMobile && hasMoreNotifications && <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2"><Button variant="outline" size="sm" onClick={() => setIsStackExpanded(!isStackExpanded)} className="bg-white/90 backdrop-blur-xs border shadow-lg">{isStackExpanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}{isStackExpanded ? "Show Less" : `${notifications.length - 3} More`}</Button></div>;
    $[25] = hasMoreNotifications;
    $[26] = isMobile;
    $[27] = isStackExpanded;
    $[28] = notifications.length;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = isStackExpanded ? `${visibleNotifications.length * 80}px` : "64px";
  let t11;
  if ($[30] !== t10) {
    t11 = <div className="pointer-events-none" style={{
      height: t10
    }} />;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7 || $[37] !== t9) {
    t12 = <div className={t4} onMouseEnter={t5} onMouseLeave={t6}>{t7}{t9}{t11}</div>;
    $[32] = t11;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t12 || $[40] !== t8) {
    t13 = <div className={t8}>{t12}</div>;
    $[39] = t12;
    $[40] = t8;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const t14 = expandedNotification !== null;
  let t15;
  let t16;
  if ($[42] !== expandedNotification || $[43] !== onDismiss) {
    t15 = open => {
      if (!open) {
        if (expandedNotification) {
          onDismiss(expandedNotification.id);
        }
        setExpandedNotification(null);
      }
    };
    t16 = expandedNotification && (() => {
      const style_0 = getNotificationStyle(expandedNotification.severity);
      const SeverityIcon_0 = style_0.icon;
      const severityLabel = expandedNotification.severity.charAt(0).toUpperCase() + expandedNotification.severity.slice(1);
      const createdLabel = new Date(expandedNotification.createdAt).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
      const dismiss = () => {
        onDismiss(expandedNotification.id);
        setExpandedNotification(null);
      };
      return <DialogContent className={cn("max-w-2xl p-0 gap-0 rounded-2xl border-0 overflow-hidden bg-white", style_0.cardShadow)}><div className="flex flex-col max-h-[80vh]"><DialogHeader className={cn("px-6 pt-6 pb-5 shrink-0 text-left", style_0.headerWash)}><DialogTitle className="flex items-start gap-3.5"><span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white", style_0.tileGlow)}><SeverityIcon_0 className={cn("h-5 w-5", style_0.accentIcon)} /></span><span className="min-w-0 pr-6"><span className="block text-lg font-semibold tracking-tight leading-tight text-[#091E42]">{expandedNotification.title}</span><span className="mt-1.5 flex items-center gap-2 text-xs font-normal text-gray-500"><span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-600 shadow-[0_1px_3px_rgba(15,23,42,0.10)]"><span className={cn("h-1.5 w-1.5 rounded-full", style_0.dot)} />{severityLabel}</span>{createdLabel}</span></span></DialogTitle></DialogHeader><div className="px-6 pt-1 pb-6 flex-1 overflow-y-auto">{expandedNotification.content && <StreamingLLMOutput content={expandedNotification.content} isStreamFinished={true} fontSize="medium" />}</div><GradientDivider /><div className="px-6 py-3.5 flex items-center justify-between gap-4 shrink-0 bg-gray-50/70"><p className="text-xs text-gray-400">This notice won't be shown again after dismissing.</p><GlassGlowButton onClick={dismiss} className="px-4">Dismiss</GlassGlowButton></div></div></DialogContent>;
    })();
    $[42] = expandedNotification;
    $[43] = onDismiss;
    $[44] = t15;
    $[45] = t16;
  } else {
    t15 = $[44];
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t14 || $[47] !== t15 || $[48] !== t16) {
    t17 = <Dialog open={t14} onOpenChange={t15}>{t16}</Dialog>;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t13 || $[51] !== t17) {
    t18 = <>{t13}{t17}</>;
    $[50] = t13;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
}
function _temp(a, b) {
  const order = ["announcement", "feature", "maintenance", "critical"] as const;
  const ai = order.indexOf(a.severity as (typeof order)[number]);
  const bi = order.indexOf(b.severity as (typeof order)[number]);
  const aIdx = ai === -1 ? -1 : ai;
  const bIdx = bi === -1 ? -1 : bi;
  return bIdx - aIdx;
}